import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowUpRight, ChevronRight, Leaf } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { FadeUp } from "@/components/Reveal";
import Breadcrumbs from "@/components/Breadcrumbs";
import { INGREDIENTS, INGREDIENT_LIST } from "@/data/ingredients";

const pick = (obj, locale) => obj[locale] || obj.fr;

const SITE_URL = "https://nanda-sa.com";

function useIngredientSeo(ing, slug, locale) {
 React.useEffect(() => {
 if (!ing) return;
 const title = pick(ing.seo.title, locale) + " | NANDA SÁ";
 const desc = pick(ing.seo.description, locale);
 const canonical = `${SITE_URL}/cosmetiques/ingredients/${slug}`;
 const ogImage = `${SITE_URL}${ing.heroImage}`;

 document.title = title;

 const setMeta = (attr, key, content) => {
 let el = document.querySelector(`meta[${attr}="${key}"]`);
 if (!el) {
 el = document.createElement("meta");
 el.setAttribute(attr, key);
 document.head.appendChild(el);
 }
 el.setAttribute("content", content);
 };
 setMeta("name", "description", desc);
 setMeta("property", "og:title", title);
 setMeta("property", "og:description", desc);
 setMeta("property", "og:image", ogImage);
 setMeta("property", "og:type", "article");
 setMeta("property", "og:url", canonical);
 setMeta("name", "twitter:card", "summary_large_image");
 setMeta("name", "twitter:title", title);
 setMeta("name", "twitter:description", desc);
 setMeta("name", "twitter:image", ogImage);

 let link = document.querySelector('link[rel="canonical"]');
 if (!link) {
 link = document.createElement("link");
 link.setAttribute("rel", "canonical");
 document.head.appendChild(link);
 }
 link.setAttribute("href", canonical);
 }, [ing, slug, locale]);
}

const Section = ({ children, className = "", id }) => (
 <section id={id} className={`border-t border-nanda-line ${className}`}>
 <div className="mx-auto max-w-[1180px] px-6 md:px-10 lg:px-14 py-20 md:py-28">{children}</div>
 </section>
);

const Overline = ({ children }) => (
 <div className="label-overline text-nanda-muted mb-4">{children}</div>
);

export default function IngredientPage() {
 const { slug } = useParams();
 const { locale } = useLanguage();
 const ing = INGREDIENTS[slug];

 useIngredientSeo(ing, slug, locale);

 if (!ing) {
 return <Navigate to="/cosmetiques" replace />;
 }

 const otherIngredients = INGREDIENT_LIST.filter((s) => s !== slug).map((s) => INGREDIENTS[s]);

 const T = {
 breadcrumbCosm: { fr: "Cosmétiques Naturels", en: "Natural Cosmetics", pt: "Cosméticos Naturais" }[locale],
 breadcrumbLib: { fr: "Bibliothèque botanique", en: "Botanical library", pt: "Biblioteca botânica" }[locale],
 infoBox: { fr: "Fiche d'identité", en: "Identity card", pt: "Ficha de identidade" }[locale],
 latinLabel: { fr: "Nom latin", en: "Latin name", pt: "Nome latino" }[locale],
 familyLabel: { fr: "Famille", en: "Family", pt: "Família" }[locale],
 partLabel: { fr: "Partie utilisée", en: "Part used", pt: "Parte utilizada" }[locale],
 originLabel: { fr: "Origine", en: "Origin", pt: "Origem" }[locale],
 productsTitle: { fr: "Retrouvez cet ingrédient dans", en: "Find this ingredient in", pt: "Encontre este ingrediente em" }[locale],
 exploreTitle: { fr: "Explorer d'autres ingrédients", en: "Explore other ingredients", pt: "Explorar outros ingredientes" }[locale],
 ctaB2B: { fr: "Demander un devis B2B", en: "Request a B2B quote", pt: "Pedir um orçamento B2B" }[locale],
 backToCol: { fr: "Retour à la collection", en: "Back to the collection", pt: "Voltar à colecção" }[locale],
 };

 // JSON-LD Article schema for SEO
 const articleSchema = {
 "@context": "https://schema.org",
 "@type": "Article",
 headline: pick(ing.seo.title, locale),
 description: pick(ing.seo.description, locale),
 inLanguage: { fr: "fr-FR", en: "en-GB", pt: "pt-PT" }[locale],
 image: `https://nanda-sa.com${ing.heroImage}`,
 author: { "@type": "Organization", name: "NANDA SÁ" },
 publisher: {
 "@type": "Organization",
 name: "NANDA SÁ",
 logo: { "@type": "ImageObject", url: "https://nanda-sa.com/logo-nanda-sa.png" },
 },
 about: {
 "@type": "Thing",
 name: ing.latin,
 description: `${ing.latin} — ${ing.family}`,
 },
 mainEntityOfPage: `https://nanda-sa.com/cosmetiques/ingredients/${slug}`,
 };

 const breadcrumbSchema = {
 "@context": "https://schema.org",
 "@type": "BreadcrumbList",
 itemListElement: [
 { "@type": "ListItem", position: 1, name: "NANDA SÁ", item: "https://nanda-sa.com/" },
 { "@type": "ListItem", position: 2, name: T.breadcrumbCosm, item: "https://nanda-sa.com/cosmetiques" },
 { "@type": "ListItem", position: 3, name: T.breadcrumbLib, item: "https://nanda-sa.com/cosmetiques#ingredients" },
 { "@type": "ListItem", position: 4, name: pick(ing.common, locale), item: `https://nanda-sa.com/cosmetiques/ingredients/${slug}` },
 ],
 };

 return (
 <div className="bg-white min-h-screen">
 <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
 <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />

 <Breadcrumbs
 items={[
 { to: "/cosmetiques", label: T.breadcrumbCosm },
 { label: pick(ing.common, locale) },
 ]}
 />

 {/* ================= HERO ================= */}
 <section className="relative bg-[#FAF7F1] overflow-hidden" data-testid={`ingredient-hero-${slug}`}>
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-16 md:py-24">
 <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
 <div className="lg:col-span-6">
 <FadeUp>
 <Overline>{T.breadcrumbLib}</Overline>
 <div className="text-xs tracking-[0.3em] uppercase text-[#B8935E] mb-3">{ing.latin}</div>
 <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[0.95] text-nanda-ink" data-testid="ingredient-title">
 {pick(ing.common, locale)}
 </h1>
 <p className="mt-6 text-lg md:text-xl text-nanda-muted italic font-serif max-w-lg leading-relaxed">
 {pick(ing.tagline, locale)}
 </p>

 <div className="mt-10 border-t border-nanda-line pt-6 max-w-sm">
 <div className="label-overline text-[#B8935E] mb-4">{T.infoBox}</div>
 <dl className="space-y-3 text-sm">
 <div className="flex justify-between gap-4 border-b border-nanda-line pb-2">
 <dt className="text-nanda-muted uppercase tracking-wider text-[10px]">{T.latinLabel}</dt>
 <dd className="font-serif italic text-nanda-ink text-right">{ing.latin}</dd>
 </div>
 <div className="flex justify-between gap-4 border-b border-nanda-line pb-2">
 <dt className="text-nanda-muted uppercase tracking-wider text-[10px]">{T.familyLabel}</dt>
 <dd className="font-serif italic text-nanda-ink text-right">{ing.family}</dd>
 </div>
 <div className="flex justify-between gap-4 border-b border-nanda-line pb-2">
 <dt className="text-nanda-muted uppercase tracking-wider text-[10px]">{T.partLabel}</dt>
 <dd className="text-nanda-ink text-right">{pick(ing.part, locale)}</dd>
 </div>
 <div className="flex justify-between gap-4">
 <dt className="text-nanda-muted uppercase tracking-wider text-[10px]">{T.originLabel}</dt>
 <dd className="text-nanda-ink text-right">Guiné-Bissau</dd>
 </div>
 </dl>
 </div>
 </FadeUp>
 </div>

 <div className="lg:col-span-6" data-testid="ingredient-hero-image">
 <div className="relative aspect-[4/5] overflow-hidden bg-[#EFE7D8]">
 <img
 src={ing.heroImage}
 alt={`${pick(ing.common, locale)} — ${ing.latin} — NANDA SÁ Guiné-Bissau`}
 className="w-full h-full object-cover"
 loading="eager"
 />
 </div>
 </div>
 </div>
 </div>
 </section>

 {/* ================= ORIGIN ================= */}
 <Section id="origin" className="bg-white">
 <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
 <div className="lg:col-span-4">
 <Overline>{`I. · ${pick(ing.origin.title, locale)}`}</Overline>
 <h2 className="font-serif text-4xl md:text-5xl leading-tight text-nanda-ink">
 {pick(ing.origin.title, locale)}
 </h2>
 <div className="mt-6 h-[1px] w-16 bg-[#B8935E]"></div>
 </div>
 <div className="lg:col-span-8">
 <p className="font-serif text-lg md:text-xl leading-relaxed text-nanda-ink whitespace-pre-line" data-testid="ingredient-origin-body">
 {pick(ing.origin.body, locale)}
 </p>
 </div>
 </div>
 </Section>

 {/* ================= VIRTUES ================= */}
 <Section id="virtues" className="bg-[#FAF7F1]">
 <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
 <div className="lg:col-span-4">
 <Overline>{`II. · ${pick(ing.virtues.title, locale)}`}</Overline>
 <h2 className="font-serif text-4xl md:text-5xl leading-tight text-nanda-ink">
 {pick(ing.virtues.title, locale)}
 </h2>
 <div className="mt-6 h-[1px] w-16 bg-[#B8935E]"></div>
 </div>
 <div className="lg:col-span-8">
 <p className="font-serif text-lg md:text-xl leading-relaxed text-nanda-ink" data-testid="ingredient-virtues-body">
 {pick(ing.virtues.body, locale)}
 </p>
 </div>
 </div>
 </Section>

 {/* ================= RITUAL ================= */}
 <Section id="ritual" className="bg-[#1E2A22] text-white">
 <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
 <div className="lg:col-span-4">
 <div className="label-overline text-white/60 mb-4">
 {`III. · ${pick(ing.ritual.title, locale)}`}
 </div>
 <h2 className="font-serif text-4xl md:text-5xl leading-tight">
 {pick(ing.ritual.title, locale)}
 </h2>
 <div className="mt-6 h-[1px] w-16 bg-[#B8935E]"></div>
 </div>
 <div className="lg:col-span-8">
 <p className="font-serif text-lg md:text-xl leading-relaxed text-white/85" data-testid="ingredient-ritual-body">
 {pick(ing.ritual.body, locale)}
 </p>

 {ing.products.length > 0 && (
 <div className="mt-14 border-t border-white/20 pt-10">
 <div className="label-overline text-[#B8935E] mb-6">{T.productsTitle}</div>
 <ul className="space-y-4">
 {ing.products.map((p) => (
 <li key={p.slug}>
 <Link
 to="/cosmetiques#collection"
 className="group inline-flex items-center gap-3 font-serif text-xl md:text-2xl text-white hover:text-[#B8935E] transition-colors"
 data-testid={`ingredient-related-${p.slug}`}
 >
 <ChevronRight className="w-4 h-4 text-[#B8935E]" />
 {p.name}
 <ArrowUpRight className="w-4 h-4 opacity-60 group-hover:opacity-100 transition-opacity" />
 </Link>
 </li>
 ))}
 </ul>
 </div>
 )}
 </div>
 </div>
 </Section>

 {/* ================= OTHER INGREDIENTS ================= */}
 <Section className="bg-white">
 <div className="flex items-end justify-between mb-14 flex-wrap gap-4">
 <div>
 <Overline>{T.breadcrumbLib}</Overline>
 <h2 className="font-serif text-3xl md:text-4xl leading-tight text-nanda-ink">
 {T.exploreTitle}
 </h2>
 </div>
 <Link
 to="/cosmetiques"
 className="text-sm uppercase tracking-[0.2em] text-[#B8935E] hover:text-[#1E2A22] transition-colors inline-flex items-center gap-2"
 data-testid="back-to-collection"
 >
 {T.backToCol} <ArrowUpRight className="w-4 h-4" />
 </Link>
 </div>

 <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
 {otherIngredients.map((o) => (
 <Link
 key={o.slug}
 to={`/cosmetiques/ingredients/${o.slug}`}
 className="group block"
 data-testid={`other-ingredient-${o.slug}`}
 >
 <div className="relative aspect-[4/5] overflow-hidden bg-[#EFE7D8] mb-5">
 <img
 src={o.heroImage}
 alt={`${pick(o.common, locale)} — ${o.latin}`}
 className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
 loading="lazy"
 />
 </div>
 <div className="flex items-baseline justify-between gap-3">
 <h3 className="font-serif text-2xl text-nanda-ink group-hover:text-[#B8935E] transition-colors">
 {pick(o.common, locale)}
 </h3>
 <span className="text-xs italic text-nanda-muted">{o.latin}</span>
 </div>
 <p className="mt-2 text-sm text-nanda-muted italic font-serif leading-snug line-clamp-2">
 {pick(o.tagline, locale)}
 </p>
 </Link>
 ))}
 </div>
 </Section>

 {/* ================= FINAL CTA ================= */}
 <Section className="bg-[#1E2A22] text-white text-center">
 <div className="max-w-3xl mx-auto">
 <div className="inline-flex items-center gap-2 mb-6 text-[#B8935E]">
 <Leaf className="w-4 h-4" />
 <span className="label-overline">NANDA SÁ Cosmetics</span>
 </div>
 <h2 className="font-serif text-4xl md:text-5xl leading-tight">
 {{
 fr: "Une gamme née d'Afrique de l'Ouest, pensée pour le monde.",
 en: "A range born from West Africa, crafted for the world.",
 pt: "Uma gama nascida da África Ocidental, pensada para o mundo.",
 }[locale]}
 </h2>
 <p className="mt-6 text-lg text-white/70">
 {{
 fr: "Distributeurs, hôtels, spas et revendeurs, contactez-nous pour découvrir nos conditions dédiées.",
 en: "Distributors, hotels, spas and resellers, contact us to discover our dedicated conditions.",
 pt: "Distribuidores, hotéis, spas e revendedores, contactem-nos para descobrir as nossas condições dedicadas.",
 }[locale]}
 </p>
 <Link
 to="/devis"
 className="inline-flex items-center gap-2 mt-10 bg-white text-nanda-ink px-8 py-4 text-sm tracking-wider uppercase hover:bg-[#B8935E] hover:text-white transition-colors"
 data-testid="ingredient-cta-b2b"
 >
 {T.ctaB2B}
 <ArrowUpRight className="w-4 h-4" />
 </Link>
 </div>
 </Section>
 </div>
 );
}
